import { motion, AnimatePresence } from 'framer-motion';
import { Palette, Check } from 'lucide-react';
import { THEMES } from '../lib/theme';
import useTheme from '../lib/useTheme';

/* ── Compact icon toggle (top bar) ── */
export function ThemeToggle({ size = 34 }) {
  const { theme, toggle, meta } = useTheme();

  return (
    <button onClick={() => { flash(); toggle(); }}
      title={`Switch to ${theme === 'roman' ? 'Neon' : 'Imperial'} theme`}
      className="relative grid place-items-center rounded-xl transition active:scale-90"
      style={{
        width: size, height: size,
        background: 'var(--accent-soft)',
        border: '1px solid var(--accent-ring)',
      }}>
      <AnimatePresence mode="wait">
        <motion.span key={theme}
          initial={{ rotate: -90, opacity: 0, scale: .6 }}
          animate={{ rotate: 0, opacity: 1, scale: 1 }}
          exit={{ rotate: 90, opacity: 0, scale: .6 }}
          transition={{ duration: .25 }}
          style={{ fontSize: size * 0.45 }}>
          {meta.icon}
        </motion.span>
      </AnimatePresence>
    </button>
  );
}

/* ── Full picker (settings / admin) ── */
export function ThemePicker({ compact }) {
  const { theme, setTheme } = useTheme();

  return (
    <div className={compact ? 'flex gap-2' : 'grid sm:grid-cols-2 gap-3'}>
      {Object.values(THEMES).map(t => {
        const active = theme === t.key;
        return (
          <motion.button key={t.key}
            onClick={() => { if (!active) { flash(); setTheme(t.key); } }}
            whileTap={{ scale: .97 }}
            className="relative p-3 text-left transition overflow-hidden"
            style={{
              borderRadius: 'var(--radius)',
              border: `2px solid ${active ? 'var(--accent)' : 'var(--border)'}`,
              background: active ? 'var(--accent-soft)' : 'var(--surface)',
              boxShadow: active ? 'var(--shadow-glow)' : 'none',
            }}>
            {active && (
              <div className="absolute top-2 right-2 w-5 h-5 rounded-full grid place-items-center text-white"
                style={{ background: 'var(--frame)' }}>
                <Check size={12} strokeWidth={3} />
              </div>
            )}

            <div className="flex items-center gap-2.5">
              <span className="text-2xl">{t.icon}</span>
              <div className="min-w-0">
                <p className="font-display font-extrabold text-sm">{t.name}</p>
                <p className="text-[11px] font-semibold" style={{ color: 'var(--text-muted)' }}>
                  {t.tagline}
                </p>
              </div>
            </div>

            {/* swatches */}
            <div className="flex gap-1 mt-2.5">
              {t.preview.map((c, i) => (
                <div key={i} className="h-6 flex-1 rounded-md"
                  style={{ background: c, boxShadow: 'inset 0 1px 0 rgba(255,255,255,.15)' }} />
              ))}
            </div>

            <p className="text-[10px] font-bold mt-2 uppercase tracking-wider"
              style={{ color: 'var(--text-muted)' }}>
              {t.mode} mode
            </p>
          </motion.button>
        );
      })}
    </div>
  );
}

/* ── transition flash ── */
function flash() {
  const el = document.createElement('div');
  el.className = 'theme-flash';
  document.body.appendChild(el);
  setTimeout(() => el.remove(), 520);
}

export default ThemePicker;